/**
 * 기본설정(ProductSettingsModal) 저장 로직.
 *
 * 모달은 사용자가 고른 "이름"(도시명·담당자명·상품타입명)을 돌려주는데, 저장 API 는 "코드"를
 * 함께 요구한다. 그 이름→코드 변환과, 모달이 건드리지 않은 나머지 필드를 원본에서 그대로
 * 실어 보내는 일을 여기서 한다. 원본을 안 실으면 저장할 때마다 안 건드린 값이 비워진다.
 *
 * 일반상품 목록과 가격/재고 화면이 같은 모달을 띄우기 때문에 한 곳에 둔다.
 */

import {
  getCities,
  getManagers,
  getProductTypes,
} from './apiService';
import { saveProductBasicInfo } from './productService';

const findByName = (list, name) =>
  (Array.isArray(list) ? list.find((i) => i.코드명 === name) : undefined);

/**
 * 모달이 돌려준 값을 저장 API 형태로 바꿔 저장한다.
 *
 * @param {object}  p
 * @param {object}  p.original 저장 전 상품 상세 (getProductDetails 결과)
 * @param {object}  p.updated  모달이 돌려준 값
 * @param {string|number} p.companyNo
 * @param {string|number} p.userNo
 */
export async function saveProductBasicSettings({ original, updated, companyNo, userNo }) {
  const base = original || {};
  const productNo = updated?.상품번호 || base.상품번호;
  if (!productNo) throw new Error('상품번호가 없습니다.');

  const [cities, managers, productTypes] = await Promise.all([
    getCities().catch(() => []),
    getManagers().catch(() => []),
    getProductTypes().catch(() => []),
  ]);

  const keywordStr = (updated?.키워드 || '').trim();
  const keywordArr = keywordStr ? keywordStr.split(',').map((k) => k.trim()).filter(Boolean) : [];

  const cityName = updated?.도시 ?? base.도시;
  const cityNo = findByName(cities, cityName)?.코드 ?? base.도시번호;

  const productTypeName = updated?.상품타입 ?? base.상품타입;
  const productTypeCode = findByName(productTypes, productTypeName)?.코드 ?? base.상품타입코드;

  const pdName = updated?.상품담당 ?? base.상품담당;
  const rdName = updated?.예약담당 ?? base.예약담당;
  const pdNo = findByName(managers, pdName)?.코드 ?? base.상품담당번호;
  const rdNo = findByName(managers, rdName)?.코드 ?? base.예약담당번호;

  const basicInfo = {
    ...base,
    상품번호: productNo,
    상품명: updated?.상품명 ?? base.상품명,
    도시번호: cityNo,
    도시: cityName,
    상품타입: productTypeName,
    상품타입코드: productTypeCode,
    상품코드: updated?.상품코드 ?? base.상품코드,
    상품코드룰사용: base.상품코드룰사용 ?? false,
    상품담당: pdName,
    상품담당번호: pdNo,
    예약담당: rdName,
    예약담당번호: rdNo,
    디폴트수배담당: (base.디폴트수배담당 ?? base.예약담당 ?? base.상품담당) || '예약담당',
    소요타입: base.소요타입 ?? base.소요시간타입 ?? base.소요단위 ?? '',
    소요: base.소요 ?? base.소요단위 ?? '',
    소요시간: updated?.소요시간 ?? base.소요시간 ?? 0,
    통화코드: base.통화코드 ?? '',
    통화: base.통화 ?? '',
    가이드타입: base.가이드타입 ?? '',
    가이드타입코드: base.가이드타입코드 ?? '',
    카테고리: base.카테고리 ?? '',
    카테고리코드: base.카테고리코드 ?? '',
    노출여부: typeof updated?.노출여부 === 'boolean' ? updated.노출여부 : (base.노출여부 ?? false),
    키워드: keywordArr,
    상품뱃지: updated?.상품뱃지 ?? base.상품뱃지,
  };

  await saveProductBasicInfo({
    companyNo,
    userNo,
    productNo,
    basicInfo,
    keywords: keywordArr.join(','),
  });

  return productNo;
}
